import { useMemo } from 'react';

const RELATED_CATEGORY_TYPE = 'related-category';

/**
 * Extract `(parent, relatedCategoryId)` pairs from the user's watchlist.
 *
 * Items have shape (matches Pop Index convention):
 *     { id, name, type: 'related-category',
 *       relatedCategoryId, categoryId? | brandId?, parentName? }
 *
 * `relatedCategoryId` is the related entity id regardless of whether it's
 * actually a category or brand — the BE pair endpoint resolves which by
 * querying both fact tables and stamping `related_type` on the response.
 */
export function useGetWatchlistRelatedCategoryPairs(watchlistItems) {
    return useMemo(() => {
        const categoryItems = [];
        const brandItems = [];

        for (const item of watchlistItems || []) {
            if (item.type !== RELATED_CATEGORY_TYPE) continue;
            if (!item.relatedCategoryId) continue;
            const baseFields = {
                id: item.id,
                name: item.name,
                parentName: item.parentName,
                relatedCategoryId: item.relatedCategoryId,
            };
            if (item.categoryId) {
                categoryItems.push({
                    ...baseFields,
                    categoryId: item.categoryId,
                });
            } else if (item.brandId) {
                brandItems.push({
                    ...baseFields,
                    brandId: item.brandId,
                });
            }
        }

        return {
            relatedCategoryParentCategoryItems: categoryItems,
            relatedCategoryParentBrandItems: brandItems,
            // Parallel arrays expected by the BE pair endpoint
            categoryPairs: categoryItems.map(
                ({ categoryId, relatedCategoryId }) => [
                    categoryId,
                    relatedCategoryId,
                ],
            ),
            brandPairs: brandItems.map(({ brandId, relatedCategoryId }) => [
                brandId,
                relatedCategoryId,
            ]),
        };
    }, [watchlistItems]);
}
